import * as React from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Menu, X } from "lucide-react";
import { Avatar, AvatarFallback, Logo } from "@dosa/ui";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { useAuth } from "../hooks/useAuth";

export interface PortalNavItem {
  key: string;
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
}

export function PortalLayout({ navItems, homeTo }: { navItems: PortalNavItem[]; homeTo: string }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const initials = user?.email?.slice(0, 2).toUpperCase() ?? "DS";

  const SidebarContent = (
    <nav className="flex flex-1 flex-col gap-1 p-3">
      {navItems.map((item) => (
        <NavLink
          key={item.key}
          to={item.to}
          end={item.to === homeTo}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-primary-50 text-primary-800" : "text-charcoal-600 hover:bg-ivory-muted"
            }`
          }
        >
          <item.icon className="h-[1.125rem] w-[1.125rem] shrink-0" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-ivory-muted/40">
      <aside className="hidden w-64 shrink-0 flex-col border-e border-charcoal-100 bg-white lg:flex">
        <Link to={homeTo} className="flex items-center border-b border-charcoal-100 p-4">
          <Logo script={i18n.language === "ar" ? "ar" : "en"} />
        </Link>
        {SidebarContent}
        <div className="border-t border-charcoal-100 p-3">
          {user?.role && (
            <p className="px-3 pb-2 text-xs font-medium uppercase tracking-wide text-charcoal-300">
              {t(`roles.${user.role.toLowerCase()}`)}
            </p>
          )}
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-charcoal-600 hover:bg-ivory-muted"
          >
            <LogOut className="h-[1.125rem] w-[1.125rem]" />
            {t("common.logout")}
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-charcoal-100 bg-white px-4 lg:px-6">
          <button type="button" className="p-2 lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Menu">
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <LanguageSwitch />
            <Avatar className="h-9 w-9">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-charcoal-900/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 start-0 flex w-72 flex-col bg-white shadow-elevated">
            <div className="flex items-center justify-between border-b border-charcoal-100 p-4">
              <Logo script={i18n.language === "ar" ? "ar" : "en"} />
              <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            {SidebarContent}
            <div className="border-t border-charcoal-100 p-3">
              <button
                type="button"
                onClick={logout}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-charcoal-600 hover:bg-ivory-muted"
              >
                <LogOut className="h-[1.125rem] w-[1.125rem]" />
                {t("common.logout")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
